import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Link,
    Breadcrumbs
} from "@mui/material";
import {useNavigate} from "react-router-dom";

const TermsOfUse: React.FC = () => {

    const navigate = useNavigate();

    const sections = [
        {
            id: "license",
            title: "License",
            content: (
                <>
                    By downloading Content from Materials Project, User agrees to accept the{" "}
                    <Link
                        href="https://creativecommons.org/licenses/by/4.0/"
                        target="_blank" // Opens in a new tab
                        rel="noopener noreferrer" // Ensures security when opening external links
                        sx={{ color: "primary.main", textDecoration: "underline" }} // Customize the color
                    >
                        Creative Commons Attribution 4.0 License
                    </Link>{" "}
                    implying that the Content may be copied, distributed, transmitted, and adapted without obtaining specific permission from the Materials Project, provided that proper attribution is given to the Materials Project, and changes (if any) are indicated.
                </>
            )
        },
        {
            id: "privacy-policy",
            title: "Privacy Policy",
            content: (
                <>
                    The CODHEM Project provides this website as a public service. This Privacy Policy outlines how the CODHEM Project collects, uses, and discloses your personal data in connection with your use of the website <Link
                    href="https://codhem.cecas.clemson.edu"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "primary.main", textDecoration: "underline" }}
                >
                    codhem.cecas.clemson.edu
                </Link> ("Website").
                    <br />
                    When we refer to "personal data" in this Privacy Policy, we mean any information that can be used to identify you, such as your name, address, location, employer, email address, or other unique identifiers.
                </>
            )
        },
        {
            id: "collection-of-personal-data",
            title: "Collection of Personal Data",
            content: (
                <>
                    When you register, we collect information about your affiliation to help us obtain funding and tailor our tools to the needs of our users. We also collect an email address to identify your profile, enabling you to review previous work and control sharing of permalinks.
                {/*    <br /><br />*/}
                {/*    When you use the Materials Project Website, our servers automatically collect the following information:*/}
                {/*    <ul>*/}
                {/*        <li>Your IP address (which may indicate your general location)</li>*/}
                {/*        <li>Your browser type and version</li>*/}
                {/*        <li>Date and time of your visit</li>*/}
                {/*        <li>Pages you view on MaterialsProject.org</li>*/}
                {/*    </ul>*/}
                {/*    Additionally, we monitor and log all site activity, including searches generated by users. By default, all user-generated data is not visible to the public unless explicitly designated otherwise by the user.*/}
                </>
            )
        },
        {
            id: "use-of-personal-data",
            title: "Use of Personal Data",
            content: (
                <>
                    We use the personal data we collect for internal purposes, such as measuring the number of visitors to various site sections and improving organization, coverage, system performance, or identifying problem areas.
                    {/*<br /><br />*/}
                    {/*This data is not used to associate search terms or navigation patterns with individual users. Occasionally, aggregated data may be used for webpage usage analysis but not for materials research. Aggregated data cannot be linked to individual users.*/}
                    <br /><br />
                    We also use Google Analytics to collect and analyze this personal data, as further explained below. We will never sell or rent your personal data, nor do we link browsing information or server logs to any personal data you submit as a registered user of The CODHEM Project.
                </>
            )
        },
        {
            id: "disclosure-of-personal-data",
            title: "Disclosure of Personal Data",
            content: (
                <>
                    The CODHEM Project does not sell or rent your personal data to third parties. Personal data may be disclosed to contractors or service providers solely for technical administration and maintenance of the Services, in compliance with applicable laws.
                    <br /><br />
                    Personal data may also be disclosed if you explicitly consent to it or if required by applicable law.
                </>
            )
        },
        {
            id: "google-analytics",
            title: "Google Analytics",
            content: (
                <>
                    The CODHEM Project uses Google Analytics, a web analytics service provided by Google, Inc. ("Google"). Google Analytics uses cookies—text files placed on your computer—to analyze how users use the Website. The information generated by these cookies (including your truncated IP address) is transmitted to and stored by Google on servers in the United States.
                    <br /><br />
                    Google uses this information to evaluate your use of the Website, compile reports on website activity, and provide other services related to website activity and internet usage. Google may transfer this information to third parties when required by law or when these parties process the information on Google’s behalf. Google does not associate your IP address with other data held by Google.
                    <br /><br />
                    By using the Website, you consent to the processing of your data by Google as described. For more information about Google Analytics, including Google's privacy policy, visit{" "}
                    <Link
                        href="https://analytics.google.com/analytics"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: "primary.main", textDecoration: "underline" }}
                    >
                        https://analytics.google.com/analytics
                    </Link>.
                    <br /><br />
                    To stop your data from being sent to Google for analytics purposes, you can install the{" "}
                    <Link
                        href="http://tools.google.com/dlpage/gaoptout?hl=en"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: "primary.main", textDecoration: "underline" }}
                    >
                        Google Analytics Browser Add-On
                    </Link>.
                </>
            )
        },
        {
            id: "cookies",
            title: "Cookies",
            content: (
                <>
                    Like most websites, we use cookies on the Website to improve efficiency and quality. Cookies help us track your visit and the pages you view.
                    <br /><br />
                    Most browsers enable cookies by default, but you can disable them or limit cookies from third parties. Note that logged-in access requires cookies.
                    <br /><br />
                    For more information about cookies, including instructions for disabling them in most browsers, visit{" "}
                    <Link
                        href="https://www.allaboutcookies.org/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: "primary.main", textDecoration: "underline" }}
                    >
                        https://www.allaboutcookies.org/
                    </Link>.
                </>
            )
        },
        {
            id: "links-to-other-sites",
            title: "Links to Other Sites",
            content: (
                <>
                    As a service to our user community, we provide links to other internet sites that offer other information. Once you follow a link to another site, you are subject to the privacy policy of that site.
                </>
            )
        },
        {
            id: "updates-to-privacy-policy",
            title: "Updates to Privacy Policy",
            content: (
                <>
                    We may occasionally update this Privacy Policy. When we do, we will revise the Effective Date below. Any changes to our Privacy Policy will always be available here on our homepage, ensuring that our visitors are always aware of what information we collect, how we might use that information, and whether we will disclose it to third parties.
                </>
            )
        },
        {
            id: "dont-want-to-give-us-your-info",
            title: "Don’t Want to Give Us Your Information?",
            content: (
                <>
                    If you decide that you do not want the CODHEM Project to collect or use your personal data in the manner described in this Privacy Policy, please do not register with our Website. If you no longer wish to be registered with the CODHEM Project, you can withdraw any previous consent at any time by emailing us.
                    <br /><br />
                    Please note that if you delete your account, all data associated with your account—including permalinks and usage data related to those searches—will be deleted.
                </>
            )
        },
        {
            id: "effective-date",
            title: "Effective Date",
            content: (
                <>
                    Effective Date: 01 January 2025.
                </>
            )
        }

    ];

    // Initialize the active section to the first section (i.e., "license")
    const [activeSection, setActiveSection] = useState<string>("license");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                let visibleSection: string = "";
                let highestPosition = Number.POSITIVE_INFINITY; // To track the topmost visible section

                entries.forEach((entry) => {
                    // Only consider sections that are intersecting and are fully within the viewport
                    if (entry.isIntersecting) {
                        // Compare the position of the section and choose the topmost
                        const rect = entry.target.getBoundingClientRect();
                        if (rect.top < highestPosition) {
                            highestPosition = rect.top;
                            visibleSection = entry.target.id;
                        }
                    }
                });

                if (visibleSection) {
                    setActiveSection(visibleSection); // Set the active section based on the topmost visible section
                }
            },
            {
                threshold: 0.10, // Only trigger when 25% of the section is visible
                rootMargin: "0px 0px -50% 0px", // Adjusted margin to trigger sooner when sections are near the bottom
            }
        );

        // Observe each section
        sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (element) observer.observe(element);
        });

        return () => {
            observer.disconnect();
        };
    }, [sections]);

    const handleLinkClick = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            setActiveSection(id); // Manually update active section on click
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f9f9f9", p: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate("/")}
                    sx={{ cursor: "pointer" }}
                >
                    Home
                </Link>
                <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate("/about/terms")}
                    sx={{ cursor: "pointer" }}
                >
                    About
                </Link>
                <Typography color="text.primary">Terms Of Use</Typography>
            </Breadcrumbs>


            <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f9f9f9", p: 3 }}>

            {/* Sidebar */}
            <Box sx={{
                position: "sticky",
                top: 16,
                width: "250px",
                mr: 4,
                alignSelf: "start",
                maxHeight: "calc(100vh - 32px)", // Adjust based on available space
                overflowY: "auto", // Ensure sidebar can scroll if needed
                borderRadius: 2, boxShadow: 3
            }}>
                <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                        Table of Contents
                    </Typography>
                    <List>
                        {sections.map((section) => (
                            <ListItem key={section.id} disablePadding>
                                <ListItemButton
                                    onClick={() => handleLinkClick(section.id)}
                                    sx={{
                                        backgroundColor: activeSection === section.id ? "primary.light" : "transparent",
                                        color: activeSection === section.id ? "primary.main" : "inherit",
                                        "&:hover": {
                                            backgroundColor: "#D3D3D3",
                                        },
                                    }}
                                >
                                    <ListItemText primary={section.title} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>

            {/* Main Content */}
            <Box sx={{ flex: 1, backgroundColor: "white", borderRadius: 2, boxShadow: 3, p: 3}}>
                <Typography variant="h4" fontWeight="bold" gutterBottom color="primary.main">
                    Terms of Use
                </Typography>
                <Typography variant="body1" gutterBottom>
                    This page contains "Terms of Use" for all users of the CODHEM website.
                    {/*There is a supplementary "Terms of Use" available{" "}*/}
                    {/*<Link href="/upload-terms" color="primary.main" underline="hover">*/}
                    {/*    here*/}
                    {/*</Link> for users who are uploading data via the MPContribs service.*/}

                </Typography>
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="body2" gutterBottom>
                        <>
                            <b>Your use of this web site is subject to the following terms and conditions:</b>
                            This website is operated by the Materials Computation & Data Science (MCDC) Group at Clemson University.
                            By using this site, you agree to comply with the terms outlined in the MCDC and Clemson Privacy and Security Notice, as well as the Materials Project Privacy Policy.
                            You acknowledge that the data in this database is computationally generated and may not be accurate for your specific application.
                            You agree not to hold the developers, contributors, or hosts of the Materials Project responsible for any inaccuracies in the data or any consequences arising from its use.
                        </>
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    {sections.map((section) => (
                        <Box
                            id={section.id}
                            key={section.id}
                            sx={{
                                mb: 4,
                                padding: 2,
                                backgroundColor: activeSection === section.id ? "rgba(0, 123, 255, 0.1)" : "transparent",
                                borderRadius: 2,
                                minHeight: "100px", // Added min-height for sections to ensure they are detected
                            }}
                        >
                            <Typography variant="h5" fontWeight="bold" gutterBottom color="secondary.main">
                                {section.title}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                {section.content}
                            </Typography>
                        </Box>
                    ))}
                    <Divider sx={{ mt: 70 }} />
                </Paper>
            </Box>
        </Box>
        </Box>
    );
};

export default TermsOfUse;
